import Box from "@mui/material/Box";
import SetupContainer from "../Setup/SetupContainer";
import { useFormContext } from "react-hook-form";
import { logoPlatform } from "../../../../utils/string";
import {
  BORDER_RADIUS,
  GOOGLE,
  IS_ADLE_DOMAIN,
  LINKEDIN,
  META,
  TIKTOK,
  TIME_LOADER_GENERATING_CAMPAIGN_ADVANCED,
} from "../../../../utils/constants";
import {
  CREATE_ADS_CAMPAIGN_LOADING_ANALYZING_INFORMATION,
  CREATE_ADS_CAMPAIGN_LOADING_FINALIZING_DETAILS,
  CREATE_ADS_CAMPAIGN_LOADING_GENERATING_RECOMMENDATIONS,
  CREATE_ADS_CAMPAIGN_LOADING_GETTING_REQUIRED_INFORMATION,
  SYNCING_INFORMATION_NO_EDIT_CLARIFICATION,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import LinearLoaderWithTime from "../../../../components/Loaders/LinearLoaderWithTime";
import AnimatedImage from "../../../../components/Media/AnimatedImage";
import CampaignMetaForm from "../../../../components/Platforms/Meta/Campaign/CampaignMetaForm";
import AdGroupMetaForm from "../../../../components/Platforms/Meta/AdGroup/AdGroupMetaForm";
import AdMetaForm from "../../../../components/Platforms/Meta/Ad/AdMetaForm";
import CampaignGoogleForm from "../../../../components/Platforms/Google/Campaign/CampaignGoogleForm";
import AdGroupGoogleForm from "../../../../components/Platforms/Google/AdGroup/AdGroupGoogleForm";
import AdGoogleForm from "../../../../components/Platforms/Google/Ad/AdGoogleForm";
import CampaignTikTokForm from "../../../../components/Platforms/TikTok/Campaign/CampaignTikTokForm";
import AdGroupTikTokForm from "../../../../components/Platforms/TikTok/AdGroup/AdGroupTikTokForm";
import AdTikTokForm from "../../../../components/Platforms/TikTok/Ad/AdTikTokForm";
import StatisticsLevel from "../Common/StatisticsLevel";
import AssetsGroupGoogleForm from "../../../../components/Platforms/Google/AssetsGroup/AssetsGroupGoogleForm";
import CampaignLinkedinForm from "../../../../components/Platforms/Linkedin/Campaign/CampaignLinkedinForm";
import AssetsGroupLinkedinForm from "../../../../components/Platforms/Linkedin/AssetsGroup/AssetsGroupLinkedinForm";
import { useLocation } from "react-router-dom";
import { keyframes } from "@emotion/react";
import { BOX_SHADOW_THEME, INPUT_COLOR_BORDER } from "../../../../utils/colors";
import usePlatformScrollPersistence from "./hooks/usePlatformScrollPersistence";
import { AdleLogoLoader } from "../../../../assets/svg/AdleLogoLoader";

const DICTIONARY_PLATFORMS_COMPONENTS = {
  [META]: {
    campaign: CampaignMetaForm,
    adGroup: AdGroupMetaForm,
    ad: AdMetaForm,
  },
  [GOOGLE]: {
    campaign: CampaignGoogleForm,
    assetsGroup: AssetsGroupGoogleForm,
    adGroup: AdGroupGoogleForm,
    ad: AdGoogleForm,
  },
  [TIKTOK]: {
    campaign: CampaignTikTokForm,
    adGroup: AdGroupTikTokForm,
    ad: AdTikTokForm,
  },
  [LINKEDIN]: {
    campaign: CampaignLinkedinForm,
    assetsGroup: AssetsGroupLinkedinForm,
  },
};

const MainContent = () => {
  const {
    isGenerating,
    platformGenerating,
    watch,
    sectionSelected,
    levelSectionSelected,
    isForcedSetupPage,
    campaignID,
    isSyncingData,
    isPlatformClicked,
  } = useFormContext();
  const { t } = useTranslationApp();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const platformURL = searchParams.get("platform");
  const isViewOnboarding = !platformURL || platformURL === "undefined";

  const platforms = watch("platforms") || [];
  const platform = sectionSelected.platform;

  const getPlatformComponent = () => {
    if (!levelSectionSelected) return null;

    const platform = sectionSelected.platform;
    const components = DICTIONARY_PLATFORMS_COMPONENTS[platform];

    if (levelSectionSelected === "platform") return components.campaign;
    if (levelSectionSelected === "assetsGroup") return components.assetsGroup;
    if (levelSectionSelected === "adGroup") return components.adGroup;
    if (levelSectionSelected === "ad") return components.ad;

    return null;
  };
  const getPlatformKey = () => {
    if (levelSectionSelected === "platform") return sectionSelected.platform;
    if (levelSectionSelected === "assetsGroup")
      return `${sectionSelected.platform}-assetsGroup-${sectionSelected.assetsGroupID}`;
    if (levelSectionSelected === "adGroup")
      return `${sectionSelected.platform}-adGroup-${sectionSelected.adGroupID}`;
    if (levelSectionSelected === "ad")
      return `${sectionSelected.platform}-adGroup-${sectionSelected.adGroupID}-ad-${sectionSelected.adID}`;

    return null;
  };

  const PlatformComponent = getPlatformComponent();
  const platformKey = getPlatformKey();
  const { onScroll, isRestoringScroll, scrollRef } =
    usePlatformScrollPersistence({
      platform,
      level: levelSectionSelected,
      platformKey,
    });

  const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

  return (
    <>
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          pt: 1.5,
          pl: { xs: 1.5, md: 3 },
          pr: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          flexDirection: "column",
        }}
      >
        {isGenerating && isViewOnboarding && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              maxWidth: 500,
              width: "100%",
            }}
          >
            {!IS_ADLE_DOMAIN && (
              <AnimatedImage
                src={logoPlatform(platformGenerating)}
                sx={{
                  width: 50,
                  height: 50,
                }}
              />
            )}
            {IS_ADLE_DOMAIN && <AdleLogoLoader />}
            <LinearLoaderWithTime
              labelSide="bottom"
              time={TIME_LOADER_GENERATING_CAMPAIGN_ADVANCED}
              platform={platformGenerating}
              max={99}
              labels={[
                t(CREATE_ADS_CAMPAIGN_LOADING_GETTING_REQUIRED_INFORMATION),
                t(CREATE_ADS_CAMPAIGN_LOADING_ANALYZING_INFORMATION),
                t(CREATE_ADS_CAMPAIGN_LOADING_GENERATING_RECOMMENDATIONS),
                t(CREATE_ADS_CAMPAIGN_LOADING_FINALIZING_DETAILS),
              ]}
            />
          </Box>
        )}
        {(platforms.length === 0 || isForcedSetupPage) && !isGenerating && (
          <SetupContainer />
        )}
        {PlatformComponent && !isForcedSetupPage && (
          <Box
            ref={scrollRef}
            onScroll={onScroll}
            sx={{
              display: "grid",
              placeItems: "flex-start",
              pb: "100px",
              pr: 1,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              gap: 2,
              opacity: isRestoringScroll ? 0 : 1,
              transition: isRestoringScroll ? "none" : "opacity 0.1s ease-in",
            }}
          >
            <StatisticsLevel
              key={`statistics-${platformKey}`}
              platform={platform}
              campaignID={campaignID}
            />
            <PlatformComponent key={`form-${platformKey}`} />
          </Box>
        )}
      </Box>

      {isSyncingData && isPlatformClicked && (
        <Box
          sx={{
            position: "fixed",
            bottom: 120,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "background.paper",
            color: "background.paperContrastText",
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            boxShadow: BOX_SHADOW_THEME,
            borderRadius: BORDER_RADIUS,
            padding: "12px 24px",
            zIndex: 9999,
            animation: `${blink} 1.5s infinite`,
          }}
        >
          {t(SYNCING_INFORMATION_NO_EDIT_CLARIFICATION)}
        </Box>
      )}
    </>
  );
};

export default MainContent;
